// client/src/v2/components/sidebar/LeftPanel.jsx

import React, { useMemo } from "react";
import SchoolListSection from "./SchoolListSection";
import HeartIcon from "./HeartIcon";
import {
  sortSchoolsByHomeDistance,
  useSchoolListBuckets,
} from "../../hooks/useSchoolListBuckets";
import { useSchoolSearch } from "../../hooks/useSchoolSearch";
import { applySchoolSearch } from "../../utils/schoolSearch";
import { UI_MSG } from "../../config/uiMessages";
import { SCHOOL_LIST_TIPS } from "./schoolListTips";

/** ~8 school rows in the scroll box (row + gap). */
const SECTION_SCROLL_PX = 370;
const LIST_INITIAL = 5;
const LIST_STEP = 5;
const LIST_MAX = 20;
/** Open To All can include every school with an Open band — allow full list. */
const OPEN_LIST_MAX = 50;

export default function LeftPanel({
  inCatchmentSchools,
  catchmentCheckBySchoolId,
  catchmentLoading,
  catchmentCheckError = null,
  schoolsError = null,
  geometryError = null,
  schoolsNear,
  openCatchment: _openCatchment,
  allSchools,
  selectedIds,
  onToggleSchool,
  onClearAllCatchments,
  homeLocation,
  transportSchool,
  onOpenTransport,
  onCloseTransport,
  onSelectRoute,
  onHoverRoute,
  onLeaveRoute,
  onLeaveOptionsList,
  onClearRoute,
  onOpenSchoolDetails,
  onSchoolRowClick,
  drawerSchoolId,
  focusedSchoolId,
  onOpenFilters,
  hasActiveFilters,
  favouriteCount = 0,
  favouritesFilterOn = false,
  onToggleFavouritesFilter,
  isFavourite,
  onToggleFavourite,
  onOpenCompare,
}) {
  const { query, setQuery, isActive } = useSchoolSearch();

  // Search must see every school, not only the nearest-20 Outside/Possible slice.
  const { topItems, middleItems, nearbyItems, bottomItems } =
    useSchoolListBuckets({
      inCatchmentSchools,
      catchmentCheckBySchoolId,
      allSchools,
      schoolsNear,
      homeLocation,
      ensureSchoolIds: selectedIds,
      uncapDistanceLists: isActive,
    });

  const selectedIdSet = useMemo(
    () => new Set(Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds]
  );

  const refine = useMemo(() => {
    return (items) => {
      let list = applySchoolSearch(items, query);
      if (favouritesFilterOn && typeof isFavourite === "function") {
        list = list.filter((s) => isFavourite(s.id));
      }
      // Map-selected schools stay visible in their bucket even if search/fav hides peers
      if (selectedIdSet.size && Array.isArray(items)) {
        const kept = new Set(list.map((s) => s.id));
        const missing = items.some(
          (s) => selectedIdSet.has(s.id) && !kept.has(s.id)
        );
        if (missing) {
          list = items.filter(
            (s) => kept.has(s.id) || selectedIdSet.has(s.id)
          );
        }
      }
      // Search ranks by name match — always re-sort every bucket by home distance
      return sortSchoolsByHomeDistance(list, homeLocation);
    };
  }, [query, favouritesFilterOn, isFavourite, selectedIdSet, homeLocation]);

  const visibleTop = useMemo(() => refine(topItems), [refine, topItems]);
  const visibleMiddle = useMemo(
    () => refine(middleItems),
    [refine, middleItems]
  );
  const visibleNearby = useMemo(
    () => refine(nearbyItems),
    [refine, nearbyItems]
  );
  const visibleBottom = useMemo(
    () => refine(bottomItems),
    [refine, bottomItems]
  );

  const searchListMax = Math.max(
    LIST_MAX,
    visibleTop.length,
    visibleMiddle.length,
    visibleNearby.length,
    visibleBottom.length,
    OPEN_LIST_MAX
  );

  // Selection is deliberately absent: Load more must stay put when a school is ticked.
  const listWindowKey = `${homeLocation?.lat ?? ""}|${homeLocation?.lon ?? ""}|${query}|${
    favouritesFilterOn ? 1 : 0
  }`;

  const limitedListProps = {
    initialVisible: LIST_INITIAL,
    loadStep: LIST_STEP,
    maxItems: isActive ? searchListMax : LIST_MAX,
    showLoadMore: true,
    scrollHeight: SECTION_SCROLL_PX,
    resetKey: listWindowKey,
  };

  const searchEmpty = UI_MSG.SEARCH_EMPTY;
  const favEmpty =
    favouriteCount === 0 ? UI_MSG.FAV_EMPTY_NONE_YET : UI_MSG.FAV_EMPTY_SECTION;

  const sectionEmpty = (fallback) => {
    if (schoolsError) return UI_MSG.SCHOOLS_LOAD_FAILED;
    if (isActive) return searchEmpty;
    if (favouritesFilterOn) return favEmpty;
    return fallback;
  };

  const favRowProps = { isFavourite, onToggleFavourite };
  const selectedCount = Array.isArray(selectedIds) ? selectedIds.length : 0;
  const compareReady = selectedCount >= 2;

  return (
    <div className="v2-left-panel">
      {/* Fixed chrome — never scrolls when school lists resize during search */}
      <div className="v2-panel-top">
        <input
          className={`v2-search${query.trim() ? " v2-search--active" : ""}`}
          type="search"
          placeholder="Search school"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search schools"
        />
        <button
          type="button"
          className={`v2-icon-btn v2-fav-filter-btn${
            favouritesFilterOn ? " v2-icon-btn--favourite" : ""
          }`}
          title={
            favouritesFilterOn
              ? "Show all schools"
              : "Show favourites only"
          }
          aria-pressed={favouritesFilterOn}
          onClick={() => onToggleFavouritesFilter?.()}
        >
          <span aria-hidden>
            <HeartIcon
              active={favouriteCount > 0 && !favouritesFilterOn}
              filled={favouritesFilterOn}
            />
          </span>
          {favouriteCount > 0 ? (
            <span className="v2-fav-filter-count">{favouriteCount}</span>
          ) : null}
        </button>
        <button
          type="button"
          className={`v2-filter-btn${hasActiveFilters ? " v2-filter-btn--active" : ""}`}
          title="Filter schools"
          onClick={onOpenFilters}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
        </button>
      </div>

      <div className="v2-left-panel-scroll">
        {schoolsError ? (
          <div className="v2-inline-alert" role="alert">
            {schoolsError}
          </div>
        ) : null}
        {catchmentCheckError ? (
          <div className="v2-inline-alert" role="alert">
            {UI_MSG.CATCHMENT_CHECK_FAILED}
          </div>
        ) : null}
        {geometryError ? (
          <div className="v2-inline-alert" role="alert">
            {geometryError}
          </div>
        ) : null}

        <SchoolListSection
          title={<>Schools In Catchment <span className="v2-section-title-count">({visibleTop.length})</span></>}
          titleTip={SCHOOL_LIST_TIPS.inCatchment}
          sectionAccent="green"
          items={visibleTop}
          selectedIds={selectedIds}
          onToggle={onToggleSchool}
          onClearAllCatchments={onClearAllCatchments}
          homeLocation={homeLocation}
          transportSchool={transportSchool}
          onOpenTransport={onOpenTransport}
          onCloseTransport={onCloseTransport}
          onSelectRoute={onSelectRoute}
          onHoverRoute={onHoverRoute}
          onLeaveRoute={onLeaveRoute}
          onLeaveOptionsList={onLeaveOptionsList}
          onClearRoute={onClearRoute}
          onOpenDetails={onOpenSchoolDetails}
          onRowClick={onSchoolRowClick}
          drawerSchoolId={drawerSchoolId}
          focusedSchoolId={focusedSchoolId}
          showCatchmentHover
          catchmentCheckBySchoolId={catchmentCheckBySchoolId}
          catchmentLoading={catchmentLoading}
          emptyText={sectionEmpty("No schools found in catchment for this home.")}
          {...favRowProps}
        />

        <SchoolListSection
          title={<>Schools Open To All <span className="v2-section-title-count">({visibleMiddle.length})</span></>}
          titleTip={SCHOOL_LIST_TIPS.openToAll}
          sectionAccent="slate"
          items={visibleMiddle}
          selectedIds={selectedIds}
          onToggle={onToggleSchool}
          onClearAllCatchments={onClearAllCatchments}
          homeLocation={homeLocation}
          transportSchool={transportSchool}
          onOpenTransport={onOpenTransport}
          onCloseTransport={onCloseTransport}
          onSelectRoute={onSelectRoute}
          onHoverRoute={onHoverRoute}
          onLeaveRoute={onLeaveRoute}
          onLeaveOptionsList={onLeaveOptionsList}
          onClearRoute={onClearRoute}
          onOpenDetails={onOpenSchoolDetails}
          onRowClick={onSchoolRowClick}
          drawerSchoolId={drawerSchoolId}
          focusedSchoolId={focusedSchoolId}
          initialVisible={LIST_INITIAL}
          loadStep={LIST_STEP}
          maxItems={isActive ? searchListMax : OPEN_LIST_MAX}
          showLoadMore
          scrollHeight={SECTION_SCROLL_PX}
          resetKey={listWindowKey}
          emptyText={sectionEmpty("No open-catchment schools to show.")}
          showOpenSeatsBracket
          {...favRowProps}
        />

        <SchoolListSection
          title={<>Possible Catchment <span className="v2-section-title-count">({visibleNearby.length})</span></>}
          titleTip={SCHOOL_LIST_TIPS.possible}
          sectionAccent="blue"
          items={visibleNearby}
          selectedIds={selectedIds}
          onToggle={onToggleSchool}
          onClearAllCatchments={onClearAllCatchments}
          homeLocation={homeLocation}
          transportSchool={transportSchool}
          onOpenTransport={onOpenTransport}
          onCloseTransport={onCloseTransport}
          onSelectRoute={onSelectRoute}
          onHoverRoute={onHoverRoute}
          onLeaveRoute={onLeaveRoute}
          onLeaveOptionsList={onLeaveOptionsList}
          onClearRoute={onClearRoute}
          onOpenDetails={onOpenSchoolDetails}
          onRowClick={onSchoolRowClick}
          drawerSchoolId={drawerSchoolId}
          focusedSchoolId={focusedSchoolId}
          {...limitedListProps}
          emptyText={sectionEmpty("No schools within ~8 miles with possible / unconfirmed catchment.")}
          {...favRowProps}
        />

        <SchoolListSection
          title={<>Schools Outside Catchment <span className="v2-section-title-count">({visibleBottom.length})</span></>}
          titleTip={SCHOOL_LIST_TIPS.outside}
          sectionAccent="yellow"
          items={visibleBottom}
          selectedIds={selectedIds}
          onToggle={onToggleSchool}
          onClearAllCatchments={onClearAllCatchments}
          homeLocation={homeLocation}
          transportSchool={transportSchool}
          onOpenTransport={onOpenTransport}
          onCloseTransport={onCloseTransport}
          onSelectRoute={onSelectRoute}
          onHoverRoute={onHoverRoute}
          onLeaveRoute={onLeaveRoute}
          onLeaveOptionsList={onLeaveOptionsList}
          onClearRoute={onClearRoute}
          onOpenDetails={onOpenSchoolDetails}
          onRowClick={onSchoolRowClick}
          drawerSchoolId={drawerSchoolId}
          focusedSchoolId={focusedSchoolId}
          {...limitedListProps}
          emptyText={sectionEmpty("No schools outside catchment to show.")}
          {...favRowProps}
        />
      </div>

      <div className="v2-left-panel-foot">
        <button
          type="button"
          className="v2-compare-btn"
          disabled={!compareReady}
          title={
            compareReady
              ? `Compare ${Math.min(selectedCount, 4)} selected school${selectedCount === 1 ? "" : "s"}`
              : "Select at least two schools to compare"
          }
          aria-disabled={!compareReady}
          onClick={() => {
            if (!compareReady) return;
            onOpenCompare?.(selectedIds);
          }}
        >
          Compare{compareReady ? ` (${Math.min(selectedCount, 4)})` : ""}
        </button>
      </div>
    </div>
  );
}
